import React, { useState, useRef } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  InputAdornment,
  CircularProgress,
  Alert,
} from "@mui/material";
import { AttachFile, Send } from "@mui/icons-material";
import { API_ENDPOINT } from "../../utils/constants";
import { getToken } from "../../utils/util";
import BackBtn from "../../widgets/BackBtn";

const SupportRequest = () => {
  const [message, setMessage] = useState("");
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null); // Add ref to control file input

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setImage(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    const formData = new FormData();
    formData.append("message", message);
    if (image) {
      formData.append("image", image);
    }

    try {
      const response = await fetch(`${API_ENDPOINT}org/support-request`, {
        method: "POST",
        headers: { authorization: `Bearer ${getToken()}` },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to submit support request");
      }

      setSuccess(true);
      setMessage("");
      setImage(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = ""; // Reset file input value
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <BackBtn />
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            BetaZen Support
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
            Submit your support request below. Our team typically responds
            within 24 hours.
          </Typography>

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Your Message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              variant="outlined"
              sx={{ mb: 3 }}
              disabled={loading}
              required
            />

            <TextField
              fullWidth
              type="file"
              label="Attach Image"
              onChange={handleImageChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AttachFile />
                  </InputAdornment>
                ),
              }}
              inputProps={{ accept: "image/*", ref: fileInputRef }} // Attach ref to input
              variant="outlined"
              sx={{ mb: 3 }}
              disabled={loading}
            />

            {image && (
              <Typography variant="body2" sx={{ mb: 2 }}>
                Selected file: {image.name}
              </Typography>
            )}

            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                endIcon={loading ? null : <Send />}
                disabled={loading || !message.trim()}
                sx={{ minWidth: 120 }}
              >
                {loading ? <CircularProgress size={24} /> : "Submit Request"}
              </Button>
            </Box>

            {success && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Your support request has been submitted successfully! We'll get
                back to you within 24 hours.
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}. Please try again or contact support directly.
              </Alert>
            )}
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 3, display: "block" }}
          >
            Need immediate assistance? Contact us at support@betazen.in
          </Typography>
        </Paper>
      </Container>
    </Container>
  );
};

export default SupportRequest;
